import type { Currency } from '@packages/domain/models/currency';
import type { Fee } from '@packages/domain/models/fee';
import type { File } from '@packages/domain/models/file';
import type { Instruction } from '@packages/domain/models/instruction';
import type { Link } from '@packages/domain/models/link';
import type { Price } from '@packages/domain/models/price';
import type { Proof } from '@packages/domain/models/proof';
import type { Purchase } from '@packages/domain/models/purchase';
import { normalizeResponse } from '@packages/domain/utils/normalize/normalize';
import { schemas } from 'constants/schemas';
import type { Field } from 'models/field';
import type { Payment } from 'models/payment';
import type { Section } from 'models/section';
import { apiV3 } from 'services/apiV3';
import type { ProofResponseBody } from 'services/payex/payments/types';

export type CreatePaymentPayload = {
  callback?: (payment: Payment) => unknown;
  orderId: string;
  token: string;
  locale: string;
  hideUserManagement?: boolean;
  captchaResponse?: string;
  platform?: string;
  recipient?: string;
  whatsappNotification?: boolean;
  promotionsOptIn?: boolean;
  expectedYearGraduation?: string;
  programType?: string;
  studentPersonalEmail?: string;
  studentMobileNumber?: string;
};

export const payments = {
  create: async ({
    orderId,
    token,
    locale,
    hideUserManagement = false,
    callback,
    captchaResponse,
    platform,
    recipient,
    whatsappNotification,
    promotionsOptIn,
    expectedYearGraduation,
    programType,
    studentPersonalEmail,
    studentMobileNumber,
  }: CreatePaymentPayload) => {
    const params = {
      orderId,
      token,
      locale,
      hideUserManagement,
      captchaResponse,
      platform,
      recipient,
      whatsappNotification,
      promotionsOptIn,
      expectedYearGraduation,
      programType,
      studentPersonalEmail,
      studentMobileNumber,
    };
    const payment = await apiV3.payments.create(params);

    if (callback) {
      callback(payment);
    }

    return normalizePayment(payment);
  },
};

type NormalizedPayment = {
  entities: {
    currencies: Record<string, Currency>;
    prices: Record<string, Price>;
    purchases: Record<string, Purchase>;
    fees: Record<string, Fee>;
    instructions: Record<string, Instruction>;
    links: Record<string, Link>;
    payments: Record<string, Payment>;
    fields?: Record<string, Field>;
    sections?: Record<string, Section>;
  };
  result: string;
};

type NormalizedTemplateFields = {
  entities: {
    fields: Record<string, Field>;
    sections: Record<string, Section>;
  };
};

export const normalizePayment = (payment: Payment): NormalizedPayment => {
  const normalizedPayment: NormalizedPayment = normalizeResponse(
    payment,
    schemas.payment,
  );

  if (payment?.offer?.templateFields) {
    const normalizedTemplateFields: NormalizedTemplateFields =
      normalizeResponse(
        payment.offer.templateFields,
        schemas.arrayOfOfferFields,
      );

    if (normalizedTemplateFields.entities) {
      Object.keys(normalizedTemplateFields.entities).forEach(entityType => {
        const entityTypeKey =
          entityType as keyof NormalizedTemplateFields['entities'];
        if (!normalizedPayment.entities[entityTypeKey]) {
          normalizedPayment.entities[entityTypeKey] = {};
        }
        Object.assign(
          normalizedPayment.entities[entityTypeKey],
          normalizedTemplateFields.entities[entityTypeKey],
        );
      });
    }
  }

  return normalizedPayment;
};

export const normalizeProofs = (
  proof: ProofResponseBody,
): {
  entities?: {
    proofs: Record<string, Proof>;
    proofFiles: Record<string, File>;
  };
  result?: string[];
} => {
  return proof.length
    ? normalizeResponse<{
        proofs: Record<string, Proof>;
        proofFiles: Record<string, File>;
      }>(proof, schemas.arrayOfProofs)
    : {};
};

export const normalizeErrors = (
  errors: { source?: string; param?: string }[],
): {
  entities?: {
    errors: Record<string, Error>;
  };
  result?: string[];
} => (errors.length ? normalizeResponse(errors, schemas.arrayOfErrors) : {});
